import { useState } from 'react';
import { Mail, MessageSquare, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from '../lib/firebase';

export function Contact() {
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSending(true);

        try {
            await addDoc(collection(db, "contact_leads"), {
                email,
                message,
                submitted_at: serverTimestamp()
            });
            setSubmitted(true);
        } catch (error) {
            console.error("Error sending message:", error);
            // Still show success to user to avoid frustration, log error internally
            setSubmitted(true);
        } finally {
            setSending(false);
        }
    };

    if (submitted) {
        return (
            <div className="max-w-md mx-auto px-6 py-20 text-center animate-in zoom-in duration-300">
                <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-10 h-10 text-green-500" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Message Received</h2>
                <p className="text-slate-400 mb-8">Thank you for reaching out. We'll get back to you shortly.</p>
                <button
                    onClick={() => { setSubmitted(false); setEmail(''); setMessage(''); }}
                    className="text-brand-primary font-medium hover:text-white transition-colors"
                >
                    Send another message
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto px-6 py-12 animate-in fade-in duration-500">
            <div className="text-center mb-12">
                <h1 className="text-4xl font-bold text-white mb-4">Contact Support</h1>
                <p className="text-slate-400">Have a question or feedback? We're listening.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 bg-slate-900/50 p-8 rounded-3xl border border-slate-800 shadow-xl">
                <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                        Email Address
                    </label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Mail className="h-5 w-5 text-slate-500" />
                        </div>
                        <input
                            type="email"
                            id="email"
                            required
                            className="block w-full pl-11 pr-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all"
                            placeholder="you@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>
                </div>

                <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-2">
                        Message
                    </label>
                    <div className="relative">
                        <div className="absolute top-4 left-4 pointer-events-none">
                            <MessageSquare className="h-5 w-5 text-slate-500" />
                        </div>
                        <textarea
                            id="message"
                            required
                            rows={5}
                            className="block w-full pl-11 pr-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all"
                            placeholder="How can we help you?"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                        />
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={sending}
                    className="w-full bg-brand-primary hover:bg-orange-600 text-white font-bold py-4 px-8 rounded-xl transition-all shadow-lg shadow-brand-primary/20 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                    {sending ? (
                        <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Sending...
                        </>
                    ) : (
                        <>
                            <Send className="w-5 h-5" />
                            Send Message
                        </>
                    )}
                </button>
            </form>
        </div>
    );
}
